import {
  buildSnapshot,
  readLiveFleet,
  revisionMetaFromSnapshot,
  type CareerSaveBlob,
  type SnapshotSource,
  type SyncSnapshot,
} from "@northstar/dispatch-core";
import { companyMembershipKey } from "./crewSnapshot";

export type LoginStateResult =
  | { ok: true; snapshot: SyncSnapshot }
  | { ok: false; error: string; status?: number };

export type LoginCareerResult =
  | { ok: true; blob: CareerSaveBlob }
  | { ok: false; error: string; status?: number };

export type LoginBoardDecision = {
  /** Null when the phone must not replace the previous board. */
  snapshot: SyncSnapshot | null;
  error: string;
  /** Stays on More when a fallback or a company mismatch is in effect. */
  boardStatus: string;
  /**
   * Server-comparable schedule hash after a real `/v1/state` pull.
   * Empty when the pull failed — do not ack the revision.
   */
  scheduleRevision: string;
};

/** Career archive → snapshot shape so the phone can render tickets without a company push. */
export function snapshotFromCareerArchive(blob: CareerSaveBlob): SyncSnapshot {
  const parsed = new Date(blob.savedAtUtc);
  const now = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const payload = blob.payload;
  const source = {
    ...payload,
    scenery: payload.scenery ?? [],
    missions: payload.missions ?? [],
    crew: payload.crew ?? [],
    fleet: readLiveFleet(payload.fleet),
    inbox: payload.inbox ?? [],
    flightLogs: payload.flightLogs ?? [],
  } as unknown as SnapshotSource;
  return buildSnapshot(source, { includeSecrets: false, now });
}

/**
 * After sign-in: the Nanode company snapshot is the board.
 * A 404 (no snapshot yet) may show the account career, and More says so.
 * Any other pull failure leaves the board alone and does not ack a revision.
 */
export function decideLoginBoard(input: {
  state: LoginStateResult;
  career: LoginCareerResult | null;
}): LoginBoardDecision {
  if (input.state.ok) {
    const snapCompany = input.state.snapshot.payload.company;
    const snapKey = companyMembershipKey(snapCompany);
    const careerCompany = input.career?.ok ? input.career.blob.payload.company : null;
    const careerKey = companyMembershipKey(careerCompany);
    let boardStatus = "";
    if (careerKey && snapKey && careerKey !== snapKey) {
      const careerName = careerCompany?.operatorName?.trim() || "another company";
      const snapName = snapCompany.operatorName?.trim() || "the joined company";
      boardStatus = `Account career is ${careerName}. This phone shows the Nanode board for ${snapName}.`;
    }
    return {
      snapshot: input.state.snapshot,
      error: "",
      boardStatus,
      scheduleRevision: revisionMetaFromSnapshot(input.state.snapshot).scheduleRevision,
    };
  }

  if (input.state.status === 404 && input.career?.ok) {
    return {
      snapshot: snapshotFromCareerArchive(input.career.blob),
      error: "",
      boardStatus:
        "Nanode has no company snapshot yet. Showing this account’s career archive until the desk publishes the board.",
      scheduleRevision: "",
    };
  }

  return {
    snapshot: null,
    error: input.state.error || "Company board did not pull.",
    boardStatus: "",
    scheduleRevision: "",
  };
}
